/**
 * Checks if a value exists in a TypeScript enum (string or numeric)
 * Ignores the keys and only checks the actual enum values
 *
 * @param enumObject - The enum object to check against
 * @param value - The value to check for existence in the enum
 * @returns boolean - True if the value exists in the enum, false otherwise
 *
 * @example
 * // String enum
 * enum Color {
 *   RED = 'red',
 *   GREEN = 'green',
 *   BLUE = 'blue'
 * }
 * isValueInEnum(Color, 'red') // true
 * isValueInEnum(Color, 'yellow') // false
 *
 * @example
 * // Numeric enum
 * enum Status {
 *   PENDING = 0,
 *   APPROVED = 1,
 *   REJECTED = 2
 * }
 * isValueInEnum(Status, 1) // true
 * isValueInEnum(Status, 5) // false
 */
export function isValueInEnum<T extends Record<string, string | number>>(
  enumObject: T,
  value: unknown
): value is T[keyof T] {
  return Object.values(enumObject).includes(value as T[keyof T]);
}

/**
 * Gets all values from an enum (excluding keys for numeric enums)
 *
 * @param enumObject - The enum object to get values from
 * @returns Array of enum values
 *
 * @example
 * enum Color {
 *   RED = 'red',
 *   GREEN = 'green',
 *   BLUE = 'blue'
 * }
 * getEnumValues(Color) // ['red', 'green', 'blue']
 */
export function getEnumValues<T extends Record<string, string | number>>(
  enumObject: T
): T[keyof T][] {
  return Object.values(enumObject) as T[keyof T][];
}

/**
 * Gets all keys from an enum
 *
 * @param enumObject - The enum object to get keys from
 * @returns Array of enum keys
 *
 * @example
 * enum Color {
 *   RED = 'red',
 *   GREEN = 'green',
 *   BLUE = 'blue'
 * }
 * getEnumKeys(Color) // ['RED', 'GREEN', 'BLUE']
 */
export function getEnumKeys<T extends Record<string, string | number>>(enumObject: T): (keyof T)[] {
  return Object.keys(enumObject) as (keyof T)[];
}

/**
 * Safely converts a value to an enum value with fallback
 *
 * @param enumObject - The enum object to convert to
 * @param value - The value to convert
 * @param fallback - Fallback value if conversion fails
 * @returns The enum value or fallback
 *
 * @example
 * enum Color {
 *   RED = 'red',
 *   GREEN = 'green',
 *   BLUE = 'blue'
 * }
 * safeEnumValue(Color, 'red', Color.RED) // 'red'
 * safeEnumValue(Color, 'yellow', Color.RED) // 'red' (fallback)
 */
export function safeEnumValue<T extends Record<string, string | number>>(
  enumObject: T,
  value: unknown,
  fallback: T[keyof T]
): T[keyof T] {
  return isValueInEnum(enumObject, value) ? value : fallback;
}

/**
 * Type guard to check if a value is a valid enum key
 *
 * @param enumObject - The enum object to check against
 * @param key - The key to check
 * @returns boolean - True if the key exists in the enum
 */
export function isValidEnumKey<T extends Record<string, string | number>>(
  enumObject: T,
  key: unknown
): key is keyof T {
  return typeof key === 'string' && key in enumObject;
}

/**
 * Gets enum value by key safely
 *
 * @param enumObject - The enum object
 * @param key - The key to get value for
 * @returns The enum value or undefined if key doesn't exist
 */
export function getEnumValueByKey<T extends Record<string, string | number>>(
  enumObject: T,
  key: unknown
): T[keyof T] | undefined {
  return isValidEnumKey(enumObject, key) ? enumObject[key] : undefined;
}

/**
 * Enum utility class with static methods for convenience
 */
export class EnumUtil {
  /**
   * Checks if a value exists in an enum
   */
  static hasValue<T extends Record<string, string | number>>(
    enumObject: T,
    value: unknown
  ): value is T[keyof T] {
    return isValueInEnum(enumObject, value);
  }

  /**
   * Gets all enum values
   */
  static getValues<T extends Record<string, string | number>>(enumObject: T): T[keyof T][] {
    return getEnumValues(enumObject);
  }

  /**
   * Gets all enum keys
   */
  static getKeys<T extends Record<string, string | number>>(enumObject: T): (keyof T)[] {
    return getEnumKeys(enumObject);
  }

  /**
   * Safely gets enum value with fallback
   */
  static safeValue<T extends Record<string, string | number>>(
    enumObject: T,
    value: unknown,
    fallback: T[keyof T]
  ): T[keyof T] {
    return safeEnumValue(enumObject, value, fallback);
  }
}
